import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  pngDimensions,
  precheckUpload,
  slotLabel,
  type MediaTheme,
  type MediaViewport,
} from "@/lib/blog/media-model";
import {
  blogUploadFormSchema,
  type BlogUploadFormInput,
  type BlogUploadFormOutput,
} from "@/lib/schemas/blog-schemas";

export interface UploadSubmission {
  file: File;
  reason: string;
  acceptDimensions: boolean;
}

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  screenshotId: string;
  locale: string;
  theme: MediaTheme;
  viewport: MediaViewport;
  uploading: boolean;
  /** Set when the last attempt failed, so the card can offer a retry (§05). */
  failure?: string | undefined;
  onSubmit: (submission: UploadSubmission) => void;
}

/**
 * Replace one variant.
 *
 * The pre-flight here is advisory: publish re-runs every check server-side
 * (Q27), so "use it anyway" cannot smuggle a wrong-shaped capture past the
 * gate. What it does buy is the shape warning arriving before the upload rather
 * than after it — and the in-slot preview, so the operator sees the incoming
 * file in the space it would occupy.
 */
export function UploadDialog({
  open,
  onOpenChange,
  screenshotId,
  locale,
  theme,
  viewport,
  uploading,
  failure,
  onSubmit,
}: UploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  const form = useForm<BlogUploadFormInput, unknown, BlogUploadFormOutput>({
    resolver: zodResolver(blogUploadFormSchema),
    defaultValues: { reason: "", acceptDimensions: false },
  });

  const precheck =
    file === null
      ? null
      : precheckUpload({
          type: file.type,
          size: file.size,
          viewport,
          dimensions: dimensions ?? undefined,
        });

  const pickFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const picked = event.target.files?.[0] ?? null;
    setFile(picked);
    setPreviewUrl(picked === null ? null : URL.createObjectURL(picked));
    setDimensions(
      picked === null ? null : pngDimensions(new Uint8Array(await picked.arrayBuffer())),
    );
  };

  const submit = form.handleSubmit((values) => {
    if (file === null) return;
    onSubmit({ file, reason: values.reason, acceptDimensions: values.acceptDimensions });
  });

  const blocked = precheck !== null && !precheck.ok && !precheck.overridable;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle>Replace {screenshotId}</DialogTitle>
          <DialogDescription>
            {locale} · {slotLabel(theme, viewport)}. PNG only. The replacement is written as a new
            revision — the old bytes stay reachable from the revision that referenced them.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
          <div className="flex flex-col gap-1">
            <Label htmlFor="upload-file">Replacement PNG</Label>
            <Input
              id="upload-file"
              type="file"
              accept="image/png"
              onChange={(event) => {
                void pickFile(event);
              }}
            />
          </div>

          {previewUrl !== null && (
            <div className="rounded-2xl border p-2" data-testid="upload-preview">
              <p className="mb-2 text-xs text-muted-foreground">
                Previewed inside the slot it would occupy
              </p>
              <img
                src={previewUrl}
                alt="Incoming capture"
                className="max-h-48 w-full object-contain"
              />
            </div>
          )}

          {precheck !== null && !precheck.ok && (
            <p
              className="text-sm text-destructive"
              role="alert"
              data-testid="upload-precheck-warning"
            >
              {precheck.message}
            </p>
          )}

          {precheck !== null && precheck.overridable && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...form.register("acceptDimensions")} />
              Use it anyway — publish will re-check this server-side
            </label>
          )}

          <div className="flex flex-col gap-1">
            <Label htmlFor="upload-reason">Why is this being replaced?</Label>
            <Input id="upload-reason" {...form.register("reason")} />
            {form.formState.errors.reason !== undefined && (
              <p className="text-xs text-destructive" role="alert">
                {form.formState.errors.reason.message}
              </p>
            )}
          </div>

          {failure !== undefined && (
            <div className="rounded-2xl border border-destructive p-3" data-testid="upload-failure">
              <p className="text-sm text-destructive">{failure}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Nothing partial was written — the variant is unchanged. Try again.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={uploading || file === null || blocked}>
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
